import Hls from "hls.js";
import { type RefObject, useEffect, useRef, useState } from "react";

interface UseHlsPlayerResult {
  isLoading: boolean;
  error: string | null;
  duration: number;
}

export function useHlsPlayer(
  videoRef: RefObject<HTMLVideoElement | null>,
  url: string,
): UseHlsPlayerResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const hlsRef = useRef<Hls | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_RETRIES = 3;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    setIsLoading(true);
    setError(null);
    retryCountRef.current = 0;

    const handleMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleDurationChange = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleError = () => {
      setError("Video playback failed");
      setIsLoading(false);
    };

    video.addEventListener("loadedmetadata", handleMetadata);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("error", handleError);

    const isHlsUrl = /\.m3u8(\?|$)/i.test(url);

    if (isHlsUrl && Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          retryCountRef.current += 1;
          if (retryCountRef.current <= MAX_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              retryTimeoutRef.current = null;
              hls.loadSource(url);
            }, 2000 * retryCountRef.current);
          } else {
            // Fallback to raw URL
            hls.destroy();
            hlsRef.current = null;
            video.src = url;
            video.load();
          }
        }
      });

      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (isHlsUrl && video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = url;
      video.load();
    } else {
      // Direct video source (mp4, mov, etc.)
      video.src = url;
      video.load();
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleMetadata);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("error", handleError);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isLoading, error, duration };
}
